import type { Acties, ActieNaam, Ik } from '../../gedeeld/types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Wat hij heeft is een koekje, en dat gaat vanzelf
 *  mee.
 *
 *  Eén deur: POST /api/c met { actie, ...lijf }. Wat erin mag en wat eruit
 *  komt staat in gedeeld/types.ts (Acties), en de helper c() hieronder leest
 *  dat: c('gesprek', { id }) geeft een { gesprek } terug en c('gesprek', {})
 *  is een typefout. Zo kan het scherm niet een veld verwachten dat de server
 *  nooit stuurt.
 *
 *  Waarom een fout hier een Fout is en geen null
 *  ---------------------------------------------
 *
 *  "De taak is aangemaakt" zeggen terwijl hij nooit aankwam, is de fout die
 *  het meest kost -- iemand denkt dat het werk is uitgezet. Elke aanroep
 *  gooit dus bij een mislukking, met de tekst van de server erbij, en het
 *  scherm laat die zien.
 * =========================================================================== */

export class Fout extends Error {
  /**
   * @param fouten  per veld wat er niet klopt, als de server dat meestuurde
   *                (relatie-wijzigen-nakijken: { ok: false, fout, fouten }).
   *                Zonder dit verdween die uitleg hier, en zag je op de kaart
   *                niet welk veld de server weigerde.
   */
  constructor(public status: number, bericht: string, public fouten: Record<string, string> | null = null) { super(bericht) }
}

/** Gaat af als de sessie verloopt; de app toont dan het inlogscherm. */
let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }
/** Voor wat niet via c() loopt (het uploaden): de sessie is weg. */
export function meldUitgelogd() { bijUitgelogd?.() }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string; fouten?: unknown } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (!a.ok || lijf.ok === false) {
    const perVeld = lijf.fouten && typeof lijf.fouten === 'object' && !Array.isArray(lijf.fouten)
      ? lijf.fouten as Record<string, string> : null
    throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`, perVeld)
  }
  return lijf as T
}

/**
 * Een actie van de functie cowork.
 *
 * `actie` gaat als laatste in het lijf, zodat een veld dat toevallig ook zo
 * heet hem nooit kan overschrijven.
 */
export async function c<A extends ActieNaam>(actie: A, lijf: Acties[A]['in']): Promise<Acties[A]['uit']> {
  let a: Response
  try {
    a = await fetch('/api/c', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...lijf, actie }),
    })
  } catch {
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  return lees<Acties[A]['uit']>(a)
}

/** Voor de acties zonder invoer: c('ik', LEEG). */
export const LEEG: Record<string, never> = {}

/* ------------------------------------------------------------------ *
 *  Wie ben ik
 * ------------------------------------------------------------------ */

export interface Sessie {
  ik: Ik
  verlooptOp: number
}

/**
 * Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag, en
 * het antwoord op "geen verbinding" is hier hetzelfde als op "niet
 * ingelogd" -- het inlogscherm, met een knop die het opnieuw probeert.
 */
export async function haalSessie(): Promise<Sessie | null> {
  try {
    const a = await fetch('/api/sessie', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean } & Sessie
    return lijf.ok && lijf.ik ? { ik: lijf.ik, verlooptOp: lijf.verlooptOp } : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* de koek vervalt vanzelf */ }
}
